import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faTimes, faCheckCircle, faCircle } from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { post, put, getlist, deleteitem } from '../../contexts/apicontexts';
import config from '../../config';

const PAGE_SIZE = 5;

const COLORS = {
    primary: "#C04C2A",
    secondary: "#EC7347",
    dark: "#4A1B13",
    grey: "#707070"
};

export default function UserModule() {
    const [modules, setModules] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [isOpen, setIsOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [errors, setErrors] = useState({});
    const [isLoder, setIsLoder] = useState(false);

    const [form, setForm] = useState({
        Id: 0, // Explicit ID field
        ModuleName: "",
        SortOrder: "",
        IsSubscribersModule: false,
    });

    useEffect(() => {
        getAllData();
    }, []);

    const getAllData = async () => {
        try {
            setIsLoder(true);
            const response = await getlist(`${config.baseurl}UserModules/getlist`);
            if (response?.status === 200) {
                setModules(response.data || []);
            }
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setIsLoder(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setIsLoder(true);

            // PAYLOAD: Now includes 'Id' for the API to identify existing records
            const payload = {
                id: form.Id, // 0 for New, actual ID for Edit
                moduleName: form.ModuleName,
                sortOrder: parseInt(form.SortOrder),
                isSubscribersModule: form.IsSubscribersModule
            };

            let response;
            if (editing) {
                // UPDATE OPERATION
                response = await put(`${config.baseurl}UserModules/update`, payload);
            } else {
                // CREATE OPERATION
                response = await post(`${config.baseurl}UserModules/add`, payload);
            }
            if (response?.status === 200 || response?.status === 201) {
                getAllData();
                closeModal();
            }
        } catch (error) {
            console.error("Operation failed", error);
        } finally {
            setIsLoder(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this module?")) {
            try {
                setIsLoder(true);
                // Standard DELETE pattern using path variable
                const response = await deleteitem(`${config.baseurl}UserModules/delete`, id);
                if (response?.status === 200) {
                    getAllData();
                }
            } catch (error) {
                console.error("Delete failed", error);
            } finally {
                setIsLoder(false);
            }
        }
    };

    const openAdd = () => {
        setEditing(null);
        setErrors({});
        setForm({ Id: 0, ModuleName: "", SortOrder: "", IsSubscribersModule: false });
        setIsOpen(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setErrors({});
        // Mapping API lowercase response to Form state
        setForm({
            Id: item.id,
            ModuleName: item.moduleName,
            SortOrder: item.sortOrder,
            IsSubscribersModule: item.isSubscribersModule,
        });
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setEditing(null);
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm({ ...form, [name]: type === "checkbox" ? checked : value });
    };

    const validate = () => {
        const err = {};
        if (!form.ModuleName?.trim()) err.ModuleName = "Module Name is required";
        if (!form.SortOrder) err.SortOrder = "Sort Order is required";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    // Pagination/Search logic remains the same
    const filtered = modules.filter((m) =>
        m.moduleName?.toLowerCase().includes(search.toLowerCase())
    );
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen relative">
            <Breadcrumb pageName={"User Module"} currentPage={"User Module"} />

            <div className="grid grid-cols-1 shadow-xl rounded-xl overflow-hidden bg-white dark:bg-gray-900 border border-gray-200">

                {/* Header */}
                <div className="card-header flex justify-between items-center px-5 py-3 border-b transition-colors duration-300 bg-gradient-to-r from-[#C04C2A] to-[#4A1B13] dark:from-[#2D120D] dark:to-[#1A1A1A] dark:border-gray-800">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">User Module List</h2>
                        {isLoder && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>

                    <button
                        onClick={openAdd}
                        className="flex items-center justify-center w-10 h-10 rounded-full transition-all transform hover:scale-110 active:scale-95 shadow-lg bg-white dark:bg-gray-200 text-[#C04C2A] dark:text-[#4A1B13]"
                    >
                        <FontAwesomeIcon icon={faPlus} className="text-lg" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-6 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
                    <input
                        type="text"
                        placeholder="Search modules by name..."
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-[#EC7347] outline-none transition-all"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {/* Table */}
                <div className="overflow-x-auto relative">
                    {isLoder && modules.length > 0 && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-black/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
                            <DotLoader color={COLORS.primary} size="w-3 h-3" />
                        </div>
                    )}
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 uppercase text-1xl font-bold">
                            <tr>
                                <th className="px-6 py-4">Module Name</th>
                                <th className="px-6 py-4">Sort Order</th>
                                <th className="px-6 py-4 text-center">System Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {paginated.map((m) => (
                                <tr key={m.id} className="hover:bg-orange-50/30 dark:hover:bg-gray-800 transition-colors">
                                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">{m.moduleName}</td>
                                    <td className="px-6 py-4">
                                        <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-mono dark:text-gray-300">{m.sortOrder}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${m.isSubscribersModule ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"}`}>
                                            <FontAwesomeIcon icon={m.isSubscribersModule ? faCheckCircle : faCircle} className="text-[10px]" />
                                            {m.isSubscribersModule ? "System" : "Standard"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right space-x-4">
                                        <button onClick={() => openEdit(m)} className="text-[#707070] hover:text-blue-600 transition-colors"><FontAwesomeIcon icon={faEdit} /></button>
                                        <button onClick={() => handleDelete(m.id)} className="text-red-400 hover:text-red-600 transition-colors"><FontAwesomeIcon icon={faTrash} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination (Simplified view) */}
                <div className="flex justify-between items-center p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50">
                    <span className="text-xs text-gray-500 font-medium">Page {page} of {totalPages || 1}</span>
                    <div className="flex gap-1">
                        {Array.from({ length: totalPages }).map((_, i) => (
                            <button key={i} onClick={() => setPage(i + 1)} className={`w-8 h-8 rounded text-xs font-bold transition-all ${page === i + 1 ? "text-white shadow-lg" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`} style={page === i + 1 ? { backgroundColor: COLORS.primary } : {}}>{i + 1}</button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal}></div>
                    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md p-8 relative shadow-2xl transform transition-all animate-in fade-in zoom-in duration-200">
                        <button type="button" onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"><FontAwesomeIcon icon={faTimes} size="lg" /></button>
                        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">{editing ? "Edit Module" : "Create New Module"}</h2>

                        <div className="space-y-5">
                            {/* Hidden ID field just for tracking (optional) */}
                            <input type="hidden" value={form.Id} />

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Module Name</label>
                                <input name="ModuleName" value={form.ModuleName} onChange={handleChange} className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 ${errors.ModuleName ? "border-red-500 ring-red-100" : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`} />
                                {errors.ModuleName && <p className="mt-1 text-xs text-red-500 font-medium">{errors.ModuleName}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Sort Order</label>
                                <input type="number" name="SortOrder" value={form.SortOrder} onChange={handleChange} className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 ${errors.SortOrder ? "border-red-500 ring-red-100" : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`} />
                                {errors.SortOrder && <p className="mt-1 text-xs text-red-500 font-medium">{errors.SortOrder}</p>}
                            </div>

                            <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                                <label className="relative flex items-center cursor-pointer">
                                    <input type="checkbox" name="IsSubscribersModule" checked={form.IsSubscribersModule} onChange={handleChange} className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#C04C2A]"></div>
                                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Subscriber Only Module</span>
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button type="button" onClick={closeModal} className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                                <button type="submit" disabled={isLoder} className="flex-1 px-4 py-3 text-white font-bold rounded-xl shadow-lg transform transition-all active:scale-95 disabled:opacity-70" style={{ background: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.secondary})` }}>
                                    {isLoder ? <DotLoader color="#fff" size="w-1.5 h-1.5" /> : (editing ? "Update Module" : "Save Module")}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}